'use client'

import { Asset } from '@/lib/assets/types'
import api from '@/lib/api/client'
import { useQuery } from '@tanstack/react-query'

type AssetDetailsResponse = {
  data: Asset[]
}

interface UseAssetDetailsOptions {
  enabled?: boolean
  ids: string[]
}

const ASSET_DETAILS_BATCH_SIZE = 100
export const ASSET_DETAILS_STALE_TIME = 5 * 60 * 1000

const uniqueAssetIds = (ids: string[]) => [...new Set(ids.filter(Boolean))]

const chunkIds = (ids: string[]) => {
  const chunks: string[][] = []

  for (let index = 0; index < ids.length; index += ASSET_DETAILS_BATCH_SIZE) {
    chunks.push(ids.slice(index, index + ASSET_DETAILS_BATCH_SIZE))
  }

  return chunks
}

export const getAssetDetailsQueryKey = (ids: string[]) => ['assets', 'details', uniqueAssetIds(ids)] as const

export const fetchAssetDetails = async (ids: string[], signal?: AbortSignal) => {
  const assetIds = uniqueAssetIds(ids)

  if (!assetIds.length) {
    return []
  }

  const responses = await Promise.all(
    chunkIds(assetIds).map((idsBatch) =>
      api.get<AssetDetailsResponse>('/assets/details', {
        params: {
          ids: idsBatch.join(','),
        },
        signal,
      })
    )
  )

  return responses.flatMap(({ data }) => data.data ?? [])
}

export const useAssetDetails = ({ enabled = true, ids }: UseAssetDetailsOptions) => {
  const assetIds = uniqueAssetIds(ids)

  return useQuery({
    queryKey: getAssetDetailsQueryKey(assetIds),
    queryFn: ({ signal }) => fetchAssetDetails(assetIds, signal),
    enabled: enabled && assetIds.length > 0,
    staleTime: ASSET_DETAILS_STALE_TIME,
  })
}
